<template>
  <ValidationObserver v-slot="{handleSubmit, invalid, reset}" ref="observer">
    <artisio-modal :visible="show" :title="computedTitle" size="md" body-class="pb-2"
                   scrollable hide-footer @show="onShow"
                   :content-in-form="false" @hidden="onHidden" modal-class="mobile-full-screen">
      <spinner :show="loading"/>
      <div class="data-wrapper w-100" v-if="!loading">
        <div class="row mb-4">
          <div class="col-md-6">
            <b-button variant="primary" @click="onMoveToLocationClick">
              {{ $t('Move to Location') }}
            </b-button>
            <span>
              <b-tooltip :target="`move-location-question-mark`"
                         :title='$t("Used to change the location of a movable storage. Please scan the QR code of the location where you would like to move the movable storage")'/>
              <font-awesome-icon id="move-location-question-mark" icon="question-circle" class="icon ml-2" size="lg"/>
            </span>
          </div>
          <div class="col-md-6">
            <b-button variant="primary" @click="onViewAllItemsClick">
              {{ $t('View All Items') }}
            </b-button>
            <span>
              <b-tooltip :target="`view-items-question-mark`" :title='$t("View all items under this location")'/>
              <font-awesome-icon id="view-items-question-mark" icon="question-circle" class="icon ml-2" size="lg"/>
            </span>
          </div>
        </div>
        <div class="row mb-2">
          <div class="col-md-6">
            <div>
              <b>{{ $t('Full List of Item Locations:') }}</b>
            </div>
            <div style="font-size: 10px">
              <i>{{ $t('Please use drag-n-drop to move item locations')}}</i>
            </div>
          </div>
          <div class="col-md-6">
            <div class="text-right">
              <b-button class="p-0" variant="link" @click="onNewItemLocationClick">
                <font-awesome-icon :icon="'plus'" style="font-size: .9em"></font-awesome-icon>
                {{ $t('New Item Location') }}
              </b-button>
            </div>
          </div>
        </div>
        <v-jstree text-field-name="name" value-field-name="uuid" ref="myTree" size="small" :data="jsTreeData"
                  draggable @item-drop="onDragEnd"  @item-click="itemClick">
          <template scope="item">
            <div style="width: 300px" class="tree-anchor-child d-flex align-items-center">
              <span class="ml-2" :class="{'font-weight-bold': item.model.uuid === itemLocationUuid}">
                {{ item.model.name }}
              </span>
              <span class="ml-auto mr-2 location-actions">
                <font-awesome-icon icon="pen" class="icon text-primary mr-2" @click="onEditClick($event, item)"/>
                <font-awesome-icon icon="plus" class="icon text-primary mr-2" @click="onAddChildClick($event, item)"/>
                <font-awesome-icon icon="trash" class="icon text-danger" @click="onDeleteClick($event, item)"/>
                </span>
            </div>
          </template>
        </v-jstree>

      </div>
    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import ArtisioModal from "./../artisio-modal/ArtisioModal";
  import {createNamespacedHelpers} from "vuex";
  import Spinner from "@/core/components/common/Spinner";
  import ItemLocationService from "@/modules/setup/branch/ItemLocationService";
  import VJstree from 'vue-jstree'
  import cloneDeep from "lodash/cloneDeep";
  import {
    ON_QR_CODE_READER_MODAL_OPEN,
    UPDATE_ITEM_LOCATION_MOVE_MODAL_DATA
  } from "@/plugins/event-bus/event-bus-names";
  import QrCodeService from "@/core/components/qr-code-generator/QrCodeService";
  import {
    MODEL_NAME_ITEM_LOCATION,
    SOURCE_ITEM_LOCATION_MOVE_MODAL
  } from "@/modules/constants";
  import BranchService from "@/modules/setup/branch/BranchService";

  const {mapActions: mapActionsGlobal, mapState: mapStateGlobal} = createNamespacedHelpers('global');
  const {mapActions: branchMapActions} = createNamespacedHelpers('setup/branch');
  const {mapActions: inventoryMapActions} = createNamespacedHelpers('inventory')

  export default {
    name: "ItemLocationMoveModal",
    components: {Spinner, ArtisioModal, VJstree},
    data() {
      return {
        showConfirmation: true,
        currentItemLocation: null,
        loading: false,
        jsTreeData: [],
        itemLocations: [],
      }
    },
    computed: {
      ...mapStateGlobal({
        currentBranch: state => state.currentBranch,
        modal: state => state.itemLocationMoveModal,
        show: state => state.itemLocationMoveModal.show,
        itemLocationUuid: state => state.itemLocationMoveModal.itemLocationUuid,
      }),
      computedTitle() {
        if (this.currentItemLocation) {
          return this.$i18n.t('Selected Item Location {name}', {name: this.currentItemLocation.name})
        }
        return this.$i18n.t('Selected Item Location')
      },
    },
    methods: {
      ...mapActionsGlobal(['hideItemLocationMoveModal']),
      ...branchMapActions(['showItemLocationModal', 'removeItemLocation']),
      ...inventoryMapActions({
        updateTableSourceInventory: 'updateTableSource',
        updateSearchModelInventory: 'updateSearchModel',
        resetSearchModelInventory: 'resetSearchModel',
        getTableDataInventory: 'getTableData',
      }),
      onHidden() {
        this.hideItemLocationMoveModal();
      },
      async onShow() {
        await this.initData()
      },
      async initData() {
        this.loading = true
        this.jsTreeData = []
        let response = await ItemLocationService.getItemLocations({
          branch_uuid: this.currentBranch.uuid, expand: "children", level: 0
        });
        if (response.success) {
          if (response.body.length > 0) {
            this.itemLocations = cloneDeep(response.body);
            this.initTree(response.body);
          } else {
            this.jsTreeData = []
          }
        }
        this.loading = false
      },
      initTree(data) {
        if (!data || data.length === 0) {
          return false;
        }

        let tmpData = data;

        this.openPathAndSubtree(tmpData, this.itemLocationUuid);

        for (let rootItem of tmpData) {
          this.prepareDataForTree(rootItem)
        }

        this.jsTreeData = tmpData;
      },
      prepareDataForTree(item) {
        item.itemLocationInfo = {
          address: item.address,
          address_2: item.address_2,
          address_3: item.address_3,
          branch_uuid: item.branch_uuid,
          city: item.city,
          country_code: item.country_code,
          is_primary: item.is_primary,
          name: item.name,
          uuid: item.uuid,
          zip_code: item.zip_code,
          state: item.state,
          email: item.email,
          phone: item.phone,
        }

        if (this.itemLocationUuid === item.uuid) {
          this.currentItemLocation = cloneDeep({
            ...item.itemLocationInfo,
            uuid: this.itemLocationUuid,
            parent_uuid: item.parent_uuid
          })
        }

        if (item.children) {
          for (let child of item.children) {
            this.prepareDataForTree(child)
          }
        }
      },
      itemClick(node, item) {
        this.$nextTick(() => {
          if (item.opened) {
            item.closeChildren()
          } else {
            item.openChildren()
          }
        })
      },
      async onDragEnd(node, item, draggedItem) {
        let requestData = {
          parent_uuid: item.itemLocationInfo.uuid,
          uuid: draggedItem.itemLocationInfo.uuid,
          branch_uuid: draggedItem.itemLocationInfo.branch_uuid,
          is_primary: draggedItem.itemLocationInfo.is_primary,
        }
        let response = await BranchService.updateItemLocation(requestData.uuid, requestData);
        if (response.success) {
          this.$toast('success', this.$i18n.t('Item location has been successfully updated'));
        }
      },
      openPathAndSubtree(nodes, targetUUID, uuidKey = "uuid", childrenKey = "children") {
        let foundInThisBranch = false;

        for (const node of nodes) {
          const children = Array.isArray(node[childrenKey]) ? node[childrenKey] : [];

          const foundHere = node[uuidKey] === targetUUID;
          const foundInChild = this.openPathAndSubtree(children, targetUUID, uuidKey, childrenKey);

          if (foundHere || foundInChild) {
            // "up": mark this node opened because it's on the path
            node.opened = true;
            foundInThisBranch = true;

            // if this is the target, also open the whole subtree "down"
            if (foundHere) {
              this.openAllDescendants(children, childrenKey);
            }
          }
        }

        return foundInThisBranch;
      },
      openAllDescendants(nodes, childrenKey = "children") {
        for (const node of nodes) {
          node.opened = true;
          const children = Array.isArray(node[childrenKey]) ? node[childrenKey] : [];
          this.openAllDescendants(children, childrenKey);
        }
      },
      onMoveToLocationClick() {
        this.$evBus.$emit(ON_QR_CODE_READER_MODAL_OPEN, {
          handler: async (result) => {
            let response = await QrCodeService.getQrCodeInfo(result)
            if (response.success) {
              if (response.body.result.model_type === MODEL_NAME_ITEM_LOCATION) {
                if (this.currentItemLocation.uuid === response.body.result.model_uuid) {
                  this.$errorDialog('You can not move the location under itself. Please, select a different location')
                } else if (this.currentItemLocation.parent_uuid === response.body.result.model_uuid) {
                  this.$errorDialog('Item location is already under scanned item location. Please, select a different location')
                } else if (ItemLocationService.isDescendant(this.itemLocations, this.currentItemLocation.uuid, response.body.result.model_uuid)) {
                  this.$errorDialog('Item location can not be made a child of any of its child locations. Please, select a different location')
                } else {
                  let requestData = {
                    parent_uuid: response.body.result.model_uuid,
                    uuid: this.currentItemLocation.uuid,
                    branch_uuid: this.currentBranch.uuid,
                    is_primary: this.currentItemLocation.is_primary,
                  }
                  response = await BranchService.updateItemLocation(requestData.uuid, requestData);
                  if (response.success) {
                    this.$toast('success', this.$i18n.t('Item location successfully moved under a different location'));
                    await this.initData()
                  }
                }
              } else {
                this.$errorDialog(this.$i18n.t('Invalid QR code'))
              }
            } else {
              if (response.body.non_field_errors) {
                this.$errorDialog(response.body.non_field_errors[0]);
              } else if (response.toastError) {
                this.$errorDialog(response.toastError)
              }
            }
          }
        });
      },
      onNewItemLocationClick() {
        this.showItemLocationModal({
          object: {branch_uuid: this.currentBranch.uuid},
          source: SOURCE_ITEM_LOCATION_MOVE_MODAL
        })
      },
      onAddChildClick($event, item) {
        $event.stopPropagation()
        this.showItemLocationModal({
          object: {branch_uuid: this.currentBranch.uuid},
          source: SOURCE_ITEM_LOCATION_MOVE_MODAL,
          parentItemLocation: item.model.itemLocationInfo
        })
      },
      onEditClick($event, item) {
        $event.stopPropagation();
        this.showItemLocationModal({
          object: item.model.itemLocationInfo,
          source: SOURCE_ITEM_LOCATION_MOVE_MODAL,
          parentItemLocation: item.model.level === 0 ? null : item.vm.$parent.model.itemLocationInfo
        });
      },
      onDeleteClick($event, item) {
        if (item.model.itemLocationInfo.uuid === this.itemLocationUuid) {
         this.$errorDialog('You cannot delete the selected location');
          return
        }

        $event.stopPropagation();
        this.$confirm(
          this.$i18n.t('Are you sure you want to delete following item location?'),
          this.$i18n.t('Item location has been successfully deleted'),
          this.removeItemLocation,
          {itemLocation: item.model.itemLocationInfo, source: SOURCE_ITEM_LOCATION_MOVE_MODAL}
        );
      },
      onViewAllItemsClick() {
        this.updateTableSourceInventory({source: "inventory"})
        this.resetSearchModelInventory()
        this.updateSearchModelInventory({item_location_uuid: this.currentItemLocation.uuid, item_location: cloneDeep(this.currentItemLocation)})
        if (this.$route.name === 'inventory') {
          this.getTableDataInventory()
        } else {
          this.$router.push({name: "inventory"})
        }
        this.hideItemLocationMoveModal()
      }
    },
    mounted() {
      this.$evBus.$on(UPDATE_ITEM_LOCATION_MOVE_MODAL_DATA, () => {
        this.initData()
      })
    }
  }
</script>

<style lang="scss" scoped>
  /deep/ .tree-anchor-child {

    &:hover {
      background-color: #e7e7e7;

      .location-actions {
        visibility: visible;
      }
    }

    .location-actions {
      visibility: hidden;
    }
  }
</style>
